import { useState } from "react";

type FormState = {
  name: string;
  email: string;
  phone: string;
  location: string;
  message: string;
};

const INITIAL: FormState = {
  name: "",
  email: "",
  phone: "",
  location: "Fairfield",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState<FormState>(INITIAL);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (key: keyof FormState) =>
    (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
    ) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const isLocalDev =
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname === "localhost";

    if (isLocalDev) {
      await new Promise((resolve) => setTimeout(resolve, 400));
      setSubmitted(true);
      setSubmitting(false);
      return;
    }

    const formData = new FormData(e.currentTarget);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          location: form.location,
          message: form.message,
          website: formData.get("website"),
        }),
      });

      const data = (await response.json()) as { ok?: boolean; error?: string };

      if (!response.ok || !data.ok) {
        throw new Error(data.error ?? "Something went wrong. Please try again.");
      }

      setSubmitted(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please call our office.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-cream-100">
      <div className="section grid gap-14 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-5">
          <p className="eyebrow">Get in touch</p>
          <h2 className="mt-4 text-3xl sm:text-4xl">
            Ready when you are.
          </h2>
          <p className="mt-5 text-sage-700/90">
            Request an appointment, ask a question, or just say hello. We'll
            be in touch within one business day.
          </p>

          <div className="mt-10 space-y-6 text-sm">
            <ContactLine label="Fairfield">
              <a href="tel:2032552677" className="font-serif text-lg text-sage-800 hover:text-blush-500">
                203-255-2677
              </a>
            </ContactLine>
            <ContactLine label="Orange">
              <a href="tel:2037990600" className="font-serif text-lg text-sage-800 hover:text-blush-500">
                203-799-0600
              </a>
            </ContactLine>
            <ContactLine label="Email">
              <a href="mailto:smile@amyortho.com" className="font-serif text-lg text-sage-800 hover:text-blush-500">
                smile@amyortho.com
              </a>
            </ContactLine>
          </div>
        </div>

        <div className="lg:col-span-7">
          {submitted ? (
            <div className="rounded-2xl border border-sage-200 bg-cream-50 p-10 text-center shadow-soft">
              <p className="font-script text-3xl text-blush-500">Thank you!</p>
              <p className="mt-3 text-sage-700/90">
                We received your request and will reach out within one business
                day. Looking forward to meeting you.
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="rounded-2xl border border-sage-100 bg-cream-50 p-6 shadow-soft sm:p-8"
            >
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                className="hidden"
                aria-hidden="true"
              />
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Full name" htmlFor="name">
                  <input
                    id="name"
                    required
                    value={form.name}
                    onChange={update("name")}
                    className="form-input"
                    type="text"
                    autoComplete="name"
                  />
                </Field>
                <Field label="Phone" htmlFor="phone">
                  <input
                    id="phone"
                    required
                    value={form.phone}
                    onChange={update("phone")}
                    className="form-input"
                    type="tel"
                    autoComplete="tel"
                  />
                </Field>
                <Field label="Email" htmlFor="email" className="sm:col-span-2">
                  <input
                    id="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    className="form-input"
                    type="email"
                    autoComplete="email"
                  />
                </Field>
                <Field label="Preferred location" htmlFor="location" className="sm:col-span-2">
                  <select
                    id="location"
                    value={form.location}
                    onChange={update("location")}
                    className="form-input"
                  >
                    <option>Fairfield</option>
                    <option>Orange</option>
                    <option>No preference</option>
                  </select>
                </Field>
                <Field label="How can we help?" htmlFor="message" className="sm:col-span-2">
                  <textarea
                    id="message"
                    rows={4}
                    value={form.message}
                    onChange={update("message")}
                    className="form-input"
                  />
                </Field>
              </div>

              {error ? (
                <p className="mt-5 rounded-lg border border-blush-200 bg-blush-50 px-4 py-3 text-sm text-sage-800">
                  {error}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={submitting}
                className="btn-primary mt-6 w-full sm:w-auto disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Sending..." : "Send request"}
              </button>
              <p className="mt-3 text-xs text-sage-700/70">
                By submitting, you agree to be contacted by Dr. Amy Orthodontics
                regarding your inquiry.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({
  label,
  htmlFor,
  className = "",
  children,
}: {
  label: string;
  htmlFor: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className={`block ${className}`}>
      <span className="text-xs font-medium uppercase tracking-wider text-sage-600">
        {label}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function ContactLine({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline gap-4">
      <span className="w-20 text-xs font-medium uppercase tracking-[0.18em] text-blush-500">
        {label}
      </span>
      <div>{children}</div>
    </div>
  );
}
